// One Benchmark case: a header (brand, frame day, case, place in the To do list, save state) over form 1.6,
// which runs in a sandboxed iframe and saves through the bridge in form-bridge.ts. Editors also get every
// Sentinel's answer, summarised and read-only.

import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GoldenAdapter } from '@/adapters/golden.adapter';
import { goldenService } from '@/services/golden.service';
import { getApiErrorMessage } from '@/lib/utils';
import { CollapseBody, CollapseHeader } from '../collapse-toggle';
import type { GoldenMe } from '@/types/golden.types';
import { GOLDEN_QUERY_KEY, benchmarkTodoQueryAtom } from './benchmark-state';
import { WITHDRAWN_MESSAGE, createBridgeListener, type BridgeContext, type SaveState } from './form-bridge';

const CASE_ID = /^\d+-[0-9a-f]{32}$/;
export const ENGINE_URL = '/benchmark/form-1.6.html';

function saveStateText(state: SaveState): string {
  if (state.kind === 'saving') return 'Saving…';
  if (state.kind === 'saved') return 'Saved';
  if (state.kind === 'submitted') return 'Answer saved';
  if (state.kind === 'failed') return state.withdrawn ? WITHDRAWN_MESSAGE : 'Not saved: retry';
  return '';
}

const ANSWER_STATUS: Record<string, string> = { submitted: 'Submitted', in_progress: 'In progress' };

/** Admins only: each Sentinel's answer to this case, as form 1.6's sheet row. No save controls. */
function AnswersPanel({ caseId }: { caseId: string }) {
  const [open, setOpen] = useState(true);
  const answersAtom = useMemo(
    () =>
      atomWithQuery(() => ({
        queryKey: [...GOLDEN_QUERY_KEY, 'answers', caseId],
        queryFn: () => goldenService.answers(caseId),
        retry: false,
      })),
    [caseId],
  );
  const answers = useAtomValue(answersAtom);
  return (
    <section aria-label="Answers" className="max-h-[40%] shrink-0 overflow-auto border-b border-gray-300 px-4 py-2">
      <CollapseHeader open={open} label="Answers" variant="chevron" onToggle={() => setOpen(!open)}>
        <h2 className="text-[13px] font-medium text-text-body">
          Answers{answers.data && <span className="ml-1 tabular-nums text-gray-800">{answers.data.length}</span>}
        </h2>
      </CollapseHeader>
      <CollapseBody open={open} className="pt-2">
        {answers.isError ? (
          <p role="alert" className="text-[13px] text-red-600">
            {getApiErrorMessage(answers.error, 'Could not load the answers.')}
          </p>
        ) : answers.isPending ? (
          <Skeleton className="h-16 w-full rounded-lg" />
        ) : answers.data.length === 0 ? (
          <p className="text-[13px] text-gray-800">No answers yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sentinel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Request type</TableHead>
                <TableHead>Next step</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Send</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Cause</TableHead>
                <TableHead>Minutes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {answers.data.map(({ reviewer, status, summary }) => (
                <TableRow key={reviewer}>
                  <TableCell>{reviewer}</TableCell>
                  <TableCell>{ANSWER_STATUS[status] ?? status}</TableCell>
                  <TableCell>{summary.requestType}</TableCell>
                  <TableCell>{summary.nextStep}</TableCell>
                  <TableCell>{summary.template}</TableCell>
                  <TableCell>{summary.send}</TableCell>
                  <TableCell>{summary.severity}</TableCell>
                  <TableCell>{summary.cause}</TableCell>
                  <TableCell className="tabular-nums">{summary.secondsTotal === null ? '—' : (summary.secondsTotal / 60).toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CollapseBody>
    </section>
  );
}

interface BenchmarkCaseProps {
  caseId: string;
  me: GoldenMe;
  onOpen: (id: string | null) => void;
  viewSwitch: React.ReactNode;
}

export function BenchmarkCase({ caseId, me, onOpen, viewSwitch }: BenchmarkCaseProps) {
  const valid = CASE_ID.test(caseId);
  const { caseAtom, answerAtom } = useMemo(
    () => ({
      caseAtom: atomWithQuery(() => ({
        queryKey: [...GOLDEN_QUERY_KEY, 'case', caseId],
        queryFn: () => goldenService.getCase(caseId),
        enabled: valid,
        retry: false,
        refetchOnWindowFocus: false,
      })),
      answerAtom: atomWithQuery(() => ({
        queryKey: [...GOLDEN_QUERY_KEY, 'answer', caseId],
        queryFn: () => goldenService.getAnswer(caseId),
        enabled: valid,
        retry: false,
        refetchOnWindowFocus: false,
      })),
    }),
    [caseId, valid],
  );
  const caseQuery = useAtomValue(caseAtom);
  const answerQuery = useAtomValue(answerAtom);
  const todo = useAtomValue(benchmarkTodoQueryAtom);
  const queryClient = useAtomValue(queryClientAtom);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });

  const caseData = caseQuery.data;
  const brand = caseData ? GoldenAdapter.brandName(caseData.accountId) : '';
  const isReviewer = me.role === 'sentinel';
  const savedStatus = answerQuery.data?.status;
  const shownState: SaveState = saveState.kind === 'idle' && savedStatus === 'step1' ? { kind: 'saved' } : saveState;
  const submitted = saveState.kind === 'submitted' || (saveState.kind === 'idle' && savedStatus === 'done');
  const todoRows = todo.data?.rows ?? [];
  const todoIndex = todoRows.findIndex((row) => row.id === caseId);
  const nextCase = todoRows.find((row) => row.id !== caseId);
  const goNext = () => onOpen(nextCase?.id ?? null);

  const onSaveState = useEffectEvent((state: SaveState) => {
    setSaveState(state);
    // Lists and counts move as the answer does; the open case and its answer stay as loaded.
    if (state.kind === 'saved' || state.kind === 'submitted') {
      void queryClient.invalidateQueries({ queryKey: [...GOLDEN_QUERY_KEY, 'cases'] });
      void queryClient.invalidateQueries({ queryKey: [...GOLDEN_QUERY_KEY, 'progress'] });
    }
  });
  const onNav = useEffectEvent((to: 'next' | 'home') => (to === 'next' ? goNext() : onOpen(null)));

  useEffect(() => {
    if (!caseData) return;
    const context: BridgeContext = {
      caseData,
      brandName: brand,
      session: { email: me.email, isReviewer, isEditor: me.role === 'editor' },
      onSaveState: (state) => onSaveState(state),
      onNav: (to) => onNav(to),
    };
    const listener = createBridgeListener(
      () => frameRef.current?.contentWindow,
      () => context,
    );
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effect events are stable; this plugin version predates useEffectEvent
  }, [caseData, brand, me.email, me.role, isReviewer]);

  const loading = valid && (caseQuery.isPending || answerQuery.isPending);
  const missing = !valid || caseQuery.isError;
  const stateText = saveStateText(shownState);

  return (
    <div className="flex h-full w-full min-w-0 flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-300 px-4 py-2">
        {viewSwitch}
        <Button variant="ghost" size="sm" onClick={() => onOpen(null)}>
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to list
        </Button>
        {caseData && (
          <span className="text-[13px] text-text-body">
            {brand} · {caseData.frameDay} · {caseData.form.case ?? caseData._id}
          </span>
        )}
        {todoIndex >= 0 && (
          <span className="text-[13px] tabular-nums text-gray-800">
            Case {todoIndex + 1} of {todoRows.length} to do
          </span>
        )}
        {!isReviewer && <span className="text-[13px] text-gray-800">Read-only: editors can't save answers.</span>}
        <span className="ml-auto flex items-center gap-3">
          <span
            aria-live="polite"
            className={shownState.kind === 'failed' ? 'text-[13px] text-red-600' : 'text-[13px] text-gray-800'}
          >
            {stateText}
          </span>
          {submitted && (
            <Button size="sm" onClick={goNext}>
              Next case
            </Button>
          )}
        </span>
      </div>
      {valid && me.role === 'editor' && <AnswersPanel caseId={caseId} />}
      {loading ? (
        <Skeleton className="m-4 h-64 rounded-lg" />
      ) : missing ? (
        <p className="p-6 text-center text-[13px] text-gray-800">This case isn't available. It may have been withdrawn.</p>
      ) : saveState.kind === 'idle' && savedStatus === 'done' ? (
        <p className="p-6 text-center text-[13px] text-gray-800">You submitted this case. Answers can't be changed after submitting.</p>
      ) : (
        // Never allow-same-origin: without it the form can't reach this page's session, storage or DOM. allow-forms only
        // lets its Submit event fire (it saves through the bridge); the frame's CSP form-action 'none' blocks any real post.
        <iframe
          ref={frameRef}
          title="Benchmark case"
          src={ENGINE_URL}
          sandbox="allow-scripts allow-forms"
          className="min-h-0 w-full flex-1 border-0"
        />
      )}
    </div>
  );
}
